import { NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { GridComponent } from '@app/core/components/grid/grid.component';
import { ValidateCellPipe } from '@app/core/pipes/validate-cell.pipe';
import { ContentLayoutComponent } from '@app/layouts/content-layout/content-layout.component';
import { HeaderComponent } from '@app/layouts/header/header.component';
import { HeaderMenuComponent } from '@app/layouts/header-menu/header-menu.component';

// ngx-bootstrap modules that are used
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { AlertModule } from 'ngx-bootstrap/alert';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { DatepickerModule } from 'ngx-bootstrap/datepicker';
import { PopoverModule } from 'ngx-bootstrap/popover';


// prime-ng modules that are used
import { FileUploadModule } from 'primeng/fileupload';
import { HelperService } from '@app/core/services/helper.service';
import { AppAutoCompleteSearchComponent } from '@app/core/components/app-auto-complete-search/app-auto-complete-search.component';
import { FileUploaderComponent } from '@app/core/components/file-uploader/file-uploader.component';
import { AgentDetailComponent } from '@app/modules/agent/pages/agent-detail/agent-detail.component';
import { NotificationComponent } from '@app/modules/message-center/pages/notification/notification.component';
import { ManageRecommendationsComponent } from '@app/modules/agent/pages/manage-recommendations/manage-recommendations.component';
import { ManageSkillComponent } from '@app/modules/agent/pages/manage-skill/manage-skill.component';
import { AddSkillComponent } from '@app/modules/agent/pages/add-skill/add-skill.component';
import { RecommendAgentComponent } from '@app/modules/agent/pages/recommend-agent/recommend-agent.component';
import { DirectMessagesComponent } from '@app/modules/message-center/pages/direct-messages/direct-messages.component';
import { UserChatWindowComponent } from '@app/modules/message-center/pages/user-chat-window/user-chat-window.component';
import { EndorseAgentSkillsComponent } from '@app/modules/agent/pages/endorse-agent-skills/endorse-agent-skills.component';
import { InboxComponent } from '@app/modules/message-center/pages/inbox/inbox.component';
import { AppTimeAgoPipe } from '@app/core/pipes/app-time-ago.pipe';
import { WINDOW_PROVIDERS } from '@app/core/providers/window.provider';
import { AgentRecommendSkillsComponent } from '@app/modules/agent/pages/agent-recommend-skills/agent-recommend-skills.component';
import { TextFieldModule } from '@angular/cdk/text-field';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ShareButtonsModule } from '@ngx-share/buttons';
import { AgentBadgeRibbonImagepathPipe } from '@app/core/pipes/agent-badge-ribbon-imagepath.pipe';
import { NgxLinkPreviewModule } from 'ngx-link-preview';
import { LinkPreviewService } from '@app/core/services/link-preview.service';
import { LinkPreviewContainerComponent } from '@app/core/components/link-preview-container/link-preview-container.component';
import { RequestCacheService } from '@app/core/services/request-cache.service';
import { CachingInterceptor } from '@app/core/interceptors/caching-interceptor';
import { OnlyNumbersDirective } from '@app/core/directives/only-numbers.directive';
import { WhatIsNewComponent } from '@app/modules/home/pages/what-is-new/what-is-new.component';
import { HomeService } from '@app/core/services/home.service';

@NgModule({
    declarations : [
      GridComponent,
      ValidateCellPipe,
      ContentLayoutComponent,
      HeaderComponent,
      HeaderMenuComponent,
      AppAutoCompleteSearchComponent,
      FileUploaderComponent,
      AppTimeAgoPipe,
      AgentDetailComponent,
      NotificationComponent,
      ManageRecommendationsComponent,
      ManageSkillComponent,
      AddSkillComponent,
      RecommendAgentComponent,
      DirectMessagesComponent,
      UserChatWindowComponent,
      EndorseAgentSkillsComponent,
      InboxComponent,
      AgentRecommendSkillsComponent,
      AgentBadgeRibbonImagepathPipe,
      LinkPreviewContainerComponent,
      OnlyNumbersDirective,
      WhatIsNewComponent
    ],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        HttpClientModule,
        TypeaheadModule.forRoot(),
        AlertModule.forRoot(),
        BsDropdownModule.forRoot(),
        AccordionModule.forRoot(),
        ModalModule.forRoot(),
        PaginationModule.forRoot(),
        TabsModule.forRoot(),
        CarouselModule.forRoot(),
        PopoverModule.forRoot(),
        DatepickerModule.forRoot(),
        TimepickerModule.forRoot(),
        // NgMultiSelectDropDownModule.forRoot(),
        ButtonsModule,
        FileUploadModule,
        TextFieldModule,
        InfiniteScrollModule,
        ShareButtonsModule,
        NgxLinkPreviewModule
    ],
    exports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      RouterModule,
      HttpClientModule,
      GridComponent,
      AppAutoCompleteSearchComponent,
      FileUploaderComponent,
      ValidateCellPipe,
      ContentLayoutComponent,
      HeaderComponent,
      TypeaheadModule,
      AlertModule,
      HeaderMenuComponent,
      BsDropdownModule,
      AccordionModule,
      ModalModule,
      PaginationModule,
      TabsModule,
      CarouselModule,
      ButtonsModule,
      FileUploadModule,
      PopoverModule,
      DatepickerModule,
      TimepickerModule,
      AppTimeAgoPipe,
      // NgMultiSelectDropDownModule,
      AgentDetailComponent,
      NotificationComponent,
      ManageRecommendationsComponent,
      ManageSkillComponent,
      AddSkillComponent,
      RecommendAgentComponent,
      DirectMessagesComponent,
      UserChatWindowComponent,
      EndorseAgentSkillsComponent,
      InboxComponent,
      AgentRecommendSkillsComponent,
      TextFieldModule,
      InfiniteScrollModule,
      ShareButtonsModule,
      AgentBadgeRibbonImagepathPipe,
      NgxLinkPreviewModule,
      LinkPreviewContainerComponent,
      OnlyNumbersDirective,
      WhatIsNewComponent
    ],
    entryComponents: [
      AgentDetailComponent,
      ManageRecommendationsComponent,
      ManageSkillComponent,
      AddSkillComponent,
      RecommendAgentComponent,
      EndorseAgentSkillsComponent,
      WhatIsNewComponent
    ],
    providers : [
      DatePipe,
      AppTimeAgoPipe,
      HelperService,
      HomeService,
      WINDOW_PROVIDERS,
      AgentBadgeRibbonImagepathPipe,
      LinkPreviewService,
      RequestCacheService,
      {
        provide: HTTP_INTERCEPTORS,
        useClass: CachingInterceptor,
        multi: true
      },
    ]
})
export class SharedModule { }
